// Desktop Manager Class for the Desktop Settings Section
// Handles business logic and state management for desktop-specific settings
// Follows the Manager and Component pattern

import { Manager } from "../../../core";
import { logger } from "../../../core";

export class DesktopManager extends Manager {
  constructor() {
    super();
    this.isInitialized = false;
    this.settings = {
      startup: false,
      tray: false,
      position: true
    };
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: "manager|desktop|init",
        color1: "cyan",
      },
      "Initializing Desktop Settings manager states"
    );

    try {
      // Load desktop settings from the database API
      await this.loadDesktopSettings();

      this.isInitialized = true;

      logger.log(
        {
          tags: "manager|desktop|ready",
          color1: "green",
        },
        "Desktop Settings manager initialized successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|init",
          color1: "red",
        },
        "Failed to initialize Desktop Settings manager:",
        error
      );
      throw error;
    }
  }

  /**
   * Load desktop settings from database
   */
  async loadDesktopSettings() {
    try {
      // Load settings from desktop database API
      if (window.databaseAPI) {
        const startup = await window.databaseAPI.getConfig('desktop-startup', false);
        const tray = await window.databaseAPI.getConfig('desktop-tray', false);
        const position = await window.databaseAPI.getConfig('desktop-position', true);

        this.settings = {
          startup,
          tray,
          position
        };
      }
    } catch (error) {
      logger.warn(
        {
          tags: "manager|desktop|settings",
          color1: "yellow",
        },
        "Failed to load desktop settings, using defaults:",
        error
      );
      // Keep default settings
    }
  }

  /**
   * Save desktop settings to database
   */
  async saveDesktopSetting(key, value) {
    try {
      if (window.databaseAPI) {
        await window.databaseAPI.setConfig(`desktop-${key}`, value);
        this.settings[key] = value;

        logger.log(
          {
            tags: "manager|desktop|settings",
            color1: "blue",
          },
          `Saved desktop setting ${key}:`, value
        );
      }
    } catch (error) {
      logger.error(
        {
          tags: "manager|desktop|settings",
          color1: "red",
        },
        `Failed to save desktop setting ${key}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Toggle a desktop setting
   */
  async toggleSetting(key) {
    const newValue = !this.settings[key];
    await this.saveDesktopSetting(key, newValue);
    return newValue;
  }

  /**
   * Get current settings
   */
  getSettings() {
    return { ...this.settings };
  }
}
